import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, GraduationCap, Users, Globe, Lock, CheckCircle2, Hourglass, XCircle } from 'lucide-react';
import { fmtDate, endTime } from '../../lib/apiError';

const StatusAction = ({ session, onRequest, onWithdraw }) => {
  const s = session.my_status;
  if (session.booking_status === 'cancelled') return <Badge className="bg-rose-100 text-rose-700 border-rose-300" data-testid={`session-status-${session.id}`}>Session cancelled</Badge>;
  if (s === 'owner') return <Badge className="bg-violet-100 text-violet-700 border-violet-300" data-testid={`session-status-${session.id}`}>Your session</Badge>;
  if (s === 'joined' || s === 'accepted') {
    return (
      <Button asChild variant="outline" className="rounded-full border-emerald-300 text-emerald-700" data-testid={`session-status-${session.id}`}>
        <Link to="/my-bookings"><CheckCircle2 className="w-4 h-4 mr-2" />Joined · View in My Bookings</Link>
      </Button>
    );
  }
  if (s === 'pending') {
    return (
      <div className="flex items-center gap-2">
        <Badge className="bg-amber-100 text-amber-700 border-amber-300" data-testid={`session-status-${session.id}`}>
          <Hourglass className="w-3.5 h-3.5 mr-1" />Request pending
        </Badge>
        {onWithdraw && session.my_request_id && (
          <Button size="sm" variant="ghost" className="rounded-full text-slate-500" onClick={() => onWithdraw(session.my_request_id)} data-testid={`withdraw-request-${session.id}`}>
            Withdraw
          </Button>
        )}
      </div>
    );
  }
  if (s === 'rejected') return <Badge className="bg-rose-100 text-rose-700 border-rose-300" data-testid={`session-status-${session.id}`}><XCircle className="w-3.5 h-3.5 mr-1" />Request declined</Badge>;
  if (!session.is_open || session.spots_left <= 0) return <Badge variant="outline" data-testid={`session-status-${session.id}`}>Not open for joining</Badge>;
  return (
    <Button onClick={() => onRequest(session)} className="bg-violet-600 hover:bg-violet-700 text-white rounded-full" data-testid={`request-join-${session.id}`}>
      Request to Join
    </Button>
  );
};

const SessionCard = ({ session, onRequest, onWithdraw, note }) => {
  const openPct = Math.round((session.filled / session.group_size) * 100);
  return (
    <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300" data-testid={`session-card-${session.id}`}>
      <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-5">
        <div className="flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-lg text-slate-900">{session.subject}</h3>
            {session.chapter && <span className="text-sm text-slate-500">· {session.chapter}</span>}
            <Badge variant="outline" className="text-xs">
              {session.visibility === 'discoverable' ? <><Globe className="w-3 h-3 mr-1" />Discoverable</> : <><Lock className="w-3 h-3 mr-1" />Invite only</>}
            </Badge>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
            <span className="flex items-center gap-1.5"><GraduationCap className="w-4 h-4" />{session.teacher_name}</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{fmtDate(session.booking_date)}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{session.start_time}–{endTime(session.start_time, session.duration_hours)}</span>
            <span className="text-slate-500">Hosted by {session.host_name}</span>
          </div>
          {session.booking_status !== 'cancelled' && <div className="flex items-center gap-3 pt-1">
            <div className="h-2 w-40 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-violet-500 rounded-full" style={{ width: `${openPct}%` }} />
            </div>
            <span className="text-xs font-medium text-slate-600 flex items-center gap-1" data-testid={`session-spots-${session.id}`}>
              <Users className="w-3.5 h-3.5" />
              {session.spots_left > 0 ? `${session.spots_left} of ${session.group_size} places open` : 'All places filled'}
            </span>
          </div>}
          {note && <p className="text-xs text-slate-500 italic">“{note}”</p>}
        </div>
        <StatusAction session={session} onRequest={onRequest} onWithdraw={onWithdraw} />
      </CardContent>
    </Card>
  );
};

export default SessionCard;
